import styled from 'styled-components';

import { Text } from '../../../../components/Text';
import { ANIM, FIXED_HEADER, IMPERSONATION_BANNER } from '../../../../theme/tokens';

/**
 * The full-width support-impersonation strip. The WHOLE strip is a button: clicking
 * anywhere opens the sign-out confirm modal (the underlined "Sign out" is the visible
 * affordance, not a separate control, so there's no nested interactive element).
 *
 * Pinned to the very TOP of the viewport (above all studio chrome); MainLayout
 * measures its height into `--ows-imp-offset` and the chrome offsets itself by that
 * amount, so the strip pushes the app down instead of overlapping it. Fixed (not in
 * flow) so it stays put while the mobile document scrolls. z-index 35 sits above the
 * mobile top bar (30) but below the Lumio drawer (45) / mobile overlay (60).
 */
export const Bar = styled.button`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 35;
  appearance: none;
  border: 0;
  margin: 0;
  font: inherit;
  cursor: pointer;
  height: ${IMPERSONATION_BANNER.height};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 16px;
  background: ${IMPERSONATION_BANNER.bg};
  color: ${IMPERSONATION_BANNER.text};
  text-align: center;
  transition: background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover,
  &:focus-visible {
    background: ${IMPERSONATION_BANNER.bgHover};
  }
  /* Keyboard focus ring that reads on the blue fill (the ink colour, inset). */
  &:focus-visible {
    outline: 2px solid ${IMPERSONATION_BANNER.text};
    outline-offset: -4px;
  }

  ${FIXED_HEADER} {
    /* Mobile: let the copy wrap onto a second line on narrow phones rather than clip
       it. The measured height keeps the chrome offset in lock-step. Clear the status-bar
       inset so the strip sits below the notch (viewport-fit=cover). */
    height: auto;
    min-height: ${IMPERSONATION_BANNER.height};
    padding: calc(8px + env(safe-area-inset-top)) 16px 8px;
  }
`;

/** Leading headset glyph (support agent). Colour is inherited (currentColor). */
export const Glyph = styled.span`
  display: inline-flex;
  flex: none;

  svg {
    display: block;
  }
`;

export const Status = styled(Text)`
  font-size: 13px;
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: 0.01em;
`;

/** The visible "Sign out" affordance — underlined, slightly bolder. Not interactive
 *  on its own; the whole Bar is the button. */
export const SignOutLink = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

/* ── Confirm modal ──────────────────────────────────────────────────────── */

export const ModalBody = styled(Text)`
  font-size: 14px;
  line-height: 1.5;
`;

/** Footer action row: Cancel (outline) + Sign out (primary), right-aligned. */
export const FooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;
